import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { completeSetup as apiCompleteSetup } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { MapPin, Smartphone, CheckCircle2, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

export const SetupPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [gpsStatus, setGpsStatus] = useState('pending'); // pending, requesting, granted, denied
    const [gpsError, setGpsError] = useState(null);

    useEffect(() => {
        // Check if already set up
        const isSetupComplete = localStorage.getItem(`setup_complete_${user?.id}`);
        if (isSetupComplete) {
            navigate('/my-orders');
        }
    }, [user, navigate]);

    const requestGpsPermission = async () => {
        setGpsStatus('requesting');
        setGpsError(null);

        if (!navigator.geolocation) {
            setGpsStatus('denied');
            setGpsError('Ihr Browser unterstützt keine GPS-Ortung.');
            return;
        }

        try {
            await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve(position);
                    },
                    (error) => {
                        reject(error);
                    },
                    { 
                        enableHighAccuracy: true, 
                        timeout: 10000,
                        maximumAge: 0
                    }
                );
            });
            
            setGpsStatus('granted');
            toast.success('GPS-Berechtigung erteilt!');
            
            // Auto-advance to next step after short delay
            setTimeout(() => setStep(2), 1500);
        } catch (error) {
            setGpsStatus('denied');
            if (error.code === 1) {
                setGpsError('GPS-Berechtigung wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.');
            } else if (error.code === 2) {
                setGpsError('GPS-Position konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.');
            } else if (error.code === 3) {
                setGpsError('Zeitüberschreitung. Bitte versuchen Sie es erneut.');
            } else {
                setGpsError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
            }
        }
    };

    const completeSetup = async () => {
        try {
            // Notify backend (which sends push to admins)
            await apiCompleteSetup();
        } catch (error) {
            // Silent fail
        }
        localStorage.setItem(`setup_complete_${user?.id}`, 'true');
        toast.success('Einrichtung abgeschlossen!');
        navigate('/my-orders');
    };

    const skipSetup = () => {
        // Don't notify admins when skipping
        localStorage.setItem(`setup_complete_${user?.id}`, 'true');
        navigate('/my-orders');
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4" data-testid="setup-page">
            <Card className="w-full max-w-lg">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                        {step === 1 ? (
                            <MapPin className="h-8 w-8 text-primary" />
                        ) : (
                            <Smartphone className="h-8 w-8 text-primary" />
                        )}
                    </div>
                    <CardTitle className="text-2xl">
                        {step === 1 ? 'GPS-Tracking einrichten' : 'App installieren'}
                    </CardTitle>
                    <CardDescription>
                        Schritt {step} von 2
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    {step === 1 && (
                        <>
                            <div className="text-center space-y-3">
                                <p className="text-muted-foreground">
                                    Damit Ihr Arbeitgeber sehen kann, wo Sie während der Arbeit sind, 
                                    benötigen wir Zugriff auf Ihren Standort.
                                </p>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                                    <strong>Hinweis:</strong> Das Tracking ist nur aktiv, wenn Sie eingestempelt sind.
                                </div>
                            </div>

                            {gpsStatus === 'pending' && (
                                <Button 
                                    onClick={requestGpsPermission} 
                                    className="w-full"
                                    size="lg"
                                    data-testid="gps-permission-button"
                                >
                                    <MapPin className="h-5 w-5 mr-2" />
                                    GPS-Berechtigung erteilen
                                </Button>
                            )}

                            {gpsStatus === 'requesting' && (
                                <Button disabled className="w-full" size="lg">
                                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                    Warte auf Berechtigung...
                                </Button>
                            )}

                            {gpsStatus === 'granted' && (
                                <div className="flex items-center justify-center gap-2 text-primary font-medium">
                                    <CheckCircle2 className="h-6 w-6" />
                                    GPS-Berechtigung erteilt!
                                </div>
                            )}

                            {gpsStatus === 'denied' && (
                                <div className="space-y-3">
                                    <div className="flex items-start gap-2 text-red-600 text-sm">
                                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                        <span>{gpsError}</span>
                                    </div>
                                    <Button 
                                        onClick={requestGpsPermission} 
                                        variant="outline"
                                        className="w-full"
                                    >
                                        Erneut versuchen
                                    </Button>
                                    <Button 
                                        onClick={() => setStep(2)} 
                                        variant="ghost"
                                        className="w-full"
                                    >
                                        Überspringen und später einrichten
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="text-center space-y-3">
                                <p className="text-muted-foreground">
                                    Fügen Sie die App zu Ihrem Startbildschirm hinzu, 
                                    um sie wie eine normale App zu nutzen.
                                </p>
                            </div>

                            <div className="bg-muted rounded-lg p-4 space-y-4">
                                <div className="space-y-2">
                                    <p className="font-medium">iPhone (Safari):</p>
                                    <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                                        <li>Tippen Sie auf das Teilen-Symbol (Quadrat mit Pfeil)</li>
                                        <li>Scrollen Sie nach unten</li>
                                        <li>Wählen Sie "Zum Home-Bildschirm"</li>
                                    </ol>
                                </div>

                                <div className="space-y-2">
                                    <p className="font-medium">Android (Chrome):</p>
                                    <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                                        <li>Tippen Sie auf die drei Punkte (Menü)</li>
                                        <li>Wählen Sie "Zum Startbildschirm hinzufügen"</li>
                                    </ol>
                                </div>
                            </div>

                            <Button 
                                onClick={completeSetup} 
                                className="w-full"
                                size="lg"
                                data-testid="complete-setup-button"
                            >
                                Einrichtung abschließen
                                <ArrowRight className="h-5 w-5 ml-2" />
                            </Button>
                        </>
                    )}

                    {step === 1 && gpsStatus !== 'denied' && (
                        <button 
                            onClick={skipSetup}
                            className="w-full text-sm text-muted-foreground hover:text-foreground"
                            data-testid="skip-setup-button"
                        >
                            Überspringen
                        </button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default SetupPage;
